import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import throttle from 'lodash.throttle';
import {
  contactButtonPositionSelector,
  isContactButtonHidden as isContactButtonHiddenSelector,
  isDefaultTriggerEnabledSelector,
  messagesBottom as messagesBottomSelector
} from '~/App/shared/selectors/global';
import { setContactButtonIsHidden } from '~/App/shared/actions/contactButton';
import { ContactButton } from '~/App/shared/interfaces/store/ContactButton';
import useOnScroll from '~/App/shared/hooks/useOnScroll';
import { isObjectViewDrawerOpen } from '~/App/shared/selectors/storeObject';

export interface FloatingButtonVisibility {
  /** Whether the floating button should be hidden from view. */
  isHidden: boolean;
  /** Number of bottom messages currently shown, used to offset the button. */
  marginBottom: number;
  /**
   * Effective CSS `bottom` value, combining any bottom messages offset with an
   * explicit `bottom` override from the contact button position.
   */
  bottom: NonNullable<ContactButton['position']['bottom']>;
  /** Full contact button position override (top/right/bottom/left). */
  buttonPosition: ContactButton['position'];
}

/**
 * Shared visibility/position logic for floating buttons anchored to the bottom
 * of the viewport (valuation button, Kundo AI chat button). Hides the button
 * while at the top of the page when the default trigger is enabled, reveals it
 * on scroll, keeps it clear of any bottom messages and keeps it out of the way
 * while the bid or reserve drawer is open.
 */
export const useFloatingButtonVisibility = (): FloatingButtonVisibility => {
  const dispatch = useDispatch();
  const messagesBottom = useSelector(messagesBottomSelector);
  const isContactButtonHidden = useSelector(isContactButtonHiddenSelector);
  const isDefaultTriggerEnabled = useSelector(isDefaultTriggerEnabledSelector);
  const buttonPosition = useSelector(contactButtonPositionSelector);
  const isBiddingDrawerOpen = useSelector(isObjectViewDrawerOpen);
  const [isAtTop, setIsAtTop] = useState(true);

  const handleScroll = useCallback(
    (current: number | undefined) => {
      const hasScrolled = (current ?? 0) < 50;

      if (hasScrolled !== isAtTop) {
        setIsAtTop(hasScrolled);
      }

      if (isDefaultTriggerEnabled && hasScrolled && isContactButtonHidden) {
        dispatch(setContactButtonIsHidden(false));
      }
    },
    [dispatch, isContactButtonHidden, isAtTop, isDefaultTriggerEnabled]
  );

  const throttledScrollHandler = throttle(handleScroll, 200);

  useOnScroll({ onScroll: throttledScrollHandler });

  const isHidden =
    isBiddingDrawerOpen ||
    (isDefaultTriggerEnabled && (isAtTop || isContactButtonHidden)) ||
    (!isDefaultTriggerEnabled && isContactButtonHidden);

  const marginBottom = messagesBottom?.length ?? 0;
  const bottom =
    buttonPosition.bottom ?? (marginBottom ? `${marginBottom * 4}rem` : '2rem');

  return { isHidden, marginBottom, bottom, buttonPosition };
};

export default useFloatingButtonVisibility;
